// Settings › Wording: every platform phrase with the organisation's own word beside it.
import type { Wording } from './i18n/catalog'
import type { SourcePhrase } from './i18n/english'
import { SOURCE_LANGUAGE } from './i18n/languages'
import { placeholdersIn } from './i18n/plural'
import type { ServerPhrase, WordingOverride } from './types'
import { DESIGN_WORDS } from '../layout/words'

export interface WordingRow {
  key: string
  en: string
  /** The platform's word in the chosen language, before the organisation's. */
  platform: string
  /** The organisation's word in the chosen language. */
  own?: string
  /** Lower-cased English, platform and own words, for search. */
  hay: string
}

/** The shipped word for a web key in lang, or undefined (catalog.shippedWord). */
export type Shipped = (key: string, lang: string) => string | undefined

/** One row per platform phrase (web and server), sorted by key. */
// Settings search keywords (`….also`) are never shown, so there is nothing to reword.
const SEARCH_HINT = /(^|\.)also(\.|$)/

export function wordingRows(
  web: readonly SourcePhrase[],
  server: readonly ServerPhrase[],
  overrides: readonly WordingOverride[],
  lang: string,
  shipped: Shipped,
): WordingRow[] {
  const own = new Map(overrides.filter((o) => o.lang === lang).map((o) => [o.key, o.text]))
  const byKey = new Map<string, { en: string; platform: string }>()
  for (const p of web) {
    if (SEARCH_HINT.test(p.key)) continue
    const legacy = lang === 'az' || lang === 'ru' ? p.legacy[lang] : undefined
    const platform = lang === SOURCE_LANGUAGE ? p.en : (shipped(p.key, lang) ?? (legacy?.trim() ? legacy : undefined) ?? p.en)
    byKey.set(p.key, { en: p.en, platform })
  }
  for (const p of server) {
    const en = p.text[SOURCE_LANGUAGE] ?? ''
    byKey.set(p.key, { en, platform: p.text[lang]?.trim() ? p.text[lang]! : en })
  }
  return [...byKey.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([key, { en, platform }]) => {
      const mine = own.get(key)
      return { key, en, platform, own: mine, hay: [en, platform, mine ?? ''].join('\n').toLocaleLowerCase() }
    })
}

/** Rows matching every word of q in English, platform or own text; changed keeps only overridden ones. */
export function findRows(rows: readonly WordingRow[], q: string, changed: boolean): WordingRow[] {
  const words = q.toLocaleLowerCase().split(/\s+/).filter(Boolean)
  return rows.filter((r) => (!changed || r.own !== undefined) && words.every((w) => r.hay.includes(w)))
}

/** The words screens and slips draw themselves, which a design may also reword (layout/words.ts). */
export const SCREEN_WORD_KEYS: ReadonlySet<string> = new Set(DESIGN_WORDS.map((w) => `kiosk.${w.id}`))

/** Only the screens' and slips' built-in words, in the registry's order. */
export function screenRows(rows: readonly WordingRow[]): WordingRow[] {
  const order = [...SCREEN_WORD_KEYS]
  return rows.filter((r) => SCREEN_WORD_KEYS.has(r.key)).sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key))
}

export type WordingProblem =
  | { kind: 'empty' }
  | { kind: 'long'; max: number }
  | { kind: 'markup' }
  | { kind: 'placeholders'; need: string[] }
  | { kind: 'onlyPlaceholders'; may: string[] }

/** The placeholders a template word may use or leave out, by phrase key; undefined for others. */
export function optionalPlaceholders(key: string): string[] | undefined {
  return DESIGN_WORDS.find((w) => `kiosk.${w.id}` === key)?.optional
}

/** What the server would refuse in an organisation's word, checked before it is sent.
 *  With optional, the word may use any of those placeholders or none (a template). */
export function wordingProblem(text: string, english: string, maxLength: number, optional?: readonly string[]): WordingProblem | null {
  const trimmed = text.trim()
  if (!trimmed) return { kind: 'empty' }
  if ([...trimmed].length > maxLength) return { kind: 'long', max: maxLength }
  if (/[<>]/.test(trimmed)) return { kind: 'markup' }
  if (optional) {
    return placeholdersIn(trimmed).every((p) => optional.includes(p)) ? null : { kind: 'onlyPlaceholders', may: [...optional] }
  }
  const need = placeholdersIn(english)
  if (placeholdersIn(trimmed).join(',') !== need.join(',')) return { kind: 'placeholders', need }
  return null
}

/** lang → key → text, as catalog.setWording takes it. */
export function wordingMap(overrides: readonly WordingOverride[]): Wording {
  const out: Wording = {}
  for (const o of overrides) (out[o.lang] ??= {})[o.key] = o.text
  return out
}

/** The overrides with saved put in place of any for the same key and language. */
export function withSaved(overrides: readonly WordingOverride[], saved: WordingOverride): WordingOverride[] {
  return [...withoutWord(overrides, saved.key, saved.lang), saved]
}

export function withoutWord(overrides: readonly WordingOverride[], key: string, lang: string): WordingOverride[] {
  return overrides.filter((o) => !(o.key === key && o.lang === lang))
}
